import {
  PropsWithChildren,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import clsx from "clsx";
import { useLocation } from "react-router-dom";

import { ChevronRight } from "lucide-react";

import { AiChatActivityProvider } from "@/ai-chat/activity/AiChatActivityContext";
import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";

import NavBar from "./NavBar";
import { SettingsBar } from "./SettingsBar";
import { SettingsWelcomeMat } from "@/Settings/SettingsWelcomeMat";
import { SettingsEnvironmentProvider } from "@/Settings/SettingsEnvironmentContext";
import { MasqueradeBar } from "@/Topbar/MasqueradeBar";
import { SimulatePermissionsBar } from "@/Topbar/SimulatePermissionsBar";
import { AnnouncementBanner } from "@/Topbar/AnnouncementBanner";
import { TopBar } from "@/Topbar/TopBar";
import { PreviewBanner } from "@/preview-mode/PreviewBanner";
import { PreviewModeBar } from "@/preview-mode/PreviewModeBar";
import {
  NavigationLayoutProvider,
  useNavigationLayout,
} from "./NavigationLayoutContext";

const COLLAPSE_BREAKPOINT = 1440;
const DRAWER_COLLAPSE_KEY = "fp_drawer_collapse";

const getInitialCollapseState = (): boolean => {
  if (typeof window === "undefined") return false;

  const stored = localStorage.getItem(DRAWER_COLLAPSE_KEY);
  if (stored !== null) {
    return stored === "true";
  }

  return window.innerWidth <= COLLAPSE_BREAKPOINT;
};

/**
 * Persistent navigation shell rendered above the router's Suspense boundary.
 * Sidebar, top bar, and banners stay mounted across page transitions while
 * only the content area swaps out.
 */
export function AuthenticatedLayout({ children }: PropsWithChildren<unknown>) {
  return (
    <NavigationLayoutProvider>
      <AiChatActivityProvider>
        <AuthenticatedLayoutShell>{children}</AuthenticatedLayoutShell>
      </AiChatActivityProvider>
    </NavigationLayoutProvider>
  );
}

function AuthenticatedLayoutShell({ children }: PropsWithChildren<unknown>) {
  const { breadcrumbs } = useNavigationLayout();
  const location = useLocation();
  const contentScrollRef = useRef<HTMLDivElement>(null);

  const [drawerCollapse, setDrawerCollapseState] = useState(
    getInitialCollapseState,
  );

  useEffect(() => {
    const stored = localStorage.getItem(DRAWER_COLLAPSE_KEY);
    if (stored !== null) {
      setDrawerCollapseState(stored === "true");
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const hasUserPreference =
        localStorage.getItem(DRAWER_COLLAPSE_KEY) !== null;
      if (!hasUserPreference) {
        setDrawerCollapseState(window.innerWidth <= COLLAPSE_BREAKPOINT);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const setDrawerCollapse = useCallback((collapsed: boolean) => {
    setDrawerCollapseState(collapsed);
    localStorage.setItem(DRAWER_COLLAPSE_KEY, String(collapsed));
  }, []);

  const isDragging = useRef(false);
  const dragStartX = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
      return;
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // True only in the organization settings section (/settings/*). A prefix match
  // deliberately excludes feature-specific settings pages like
  // /session-replay/settings, which keep the normal app nav and must not show
  // the settings sidebar or the settings welcome mat.
  const isOrganizationSettings = location.pathname.startsWith("/settings");

  // Remember the most recent non-settings location so SettingsBar's "Back to
  // app" button can return there. Updated only while outside the settings
  // section, so it holds the page the user came from once they enter settings.
  const lastAppPathRef = useRef<string>("/");
  useEffect(() => {
    if (!isOrganizationSettings) {
      lastAppPathRef.current =
        location.pathname + location.search + location.hash;
    }
  }, [
    isOrganizationSettings,
    location.pathname,
    location.search,
    location.hash,
  ]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;

      const deltaX = e.clientX - dragStartX.current;
      const threshold = 50;

      if (Math.abs(deltaX) > threshold) {
        setDrawerCollapse(deltaX < 0);
        isDragging.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [setDrawerCollapse]);

  const shell = (
    <div className="relative flex h-dvh">
      <aside
        className={clsx(
          "group bg-nav-shell relative z-10 h-full flex-none transition-[width] duration-550 ease-[cubic-bezier(0.16,1,0.3,1)]",
        )}
        style={{ width: drawerCollapse ? 56 : 250 }}
      >
        {isOrganizationSettings ? (
          <SettingsBar
            drawerCollapse={drawerCollapse}
            setDrawerCollapse={setDrawerCollapse}
            backToAppPath={lastAppPathRef.current}
          />
        ) : (
          <NavBar
            drawerCollapse={drawerCollapse}
            setDrawerCollapse={setDrawerCollapse}
          />
        )}
        <div
          className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent transition-colors hover:bg-blue-400"
          onMouseDown={handleMouseDown}
          onClick={() => setDrawerCollapse(!drawerCollapse)}
          title={drawerCollapse ? "Expand sidebar" : "Collapse sidebar"}
        />
      </aside>

      <Tooltip content="Expand navigation" triggerProps={{ asChild: true }}>
        <button
          type="button"
          onClick={() => setDrawerCollapse(false)}
          aria-label="Expand navigation"
          aria-hidden={!drawerCollapse}
          tabIndex={drawerCollapse ? 0 : -1}
          style={{
            left: drawerCollapse ? 46 : 230,
            opacity: drawerCollapse ? 1 : 0,
          }}
          className={clsx(
            "z-nav-float border-borderSubtle bg-surface-default text-textSubtle hover:border-borderDefault hover:text-textSecondary fixed top-[24px] flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border shadow-md transition-[left,opacity] duration-550 ease-[cubic-bezier(0.16,1,0.3,1)] hover:shadow-lg",
            !drawerCollapse && "pointer-events-none",
          )}
        >
          <ChevronRight size={11} strokeWidth={2.5} />
        </button>
      </Tooltip>

      <main className="bg-background flex h-full flex-1 flex-col overflow-x-hidden">
        <AnnouncementBanner />
        <MasqueradeBar />
        <SimulatePermissionsBar />
        <PreviewModeBar />
        <TopBar breadcrumbs={breadcrumbs} />
        <PreviewBanner />

        {isOrganizationSettings && <SettingsWelcomeMat />}

        <div
          ref={contentScrollRef}
          className="relative z-0 min-h-0 flex-1 overflow-y-auto p-8"
        >
          {children}
        </div>
      </main>
    </div>
  );

  // Scope the settings picker to the settings section only: inside settings the
  // picker re-targets which environment the settings pages edit, without
  // switching the app's global environment. The provider mounts only here, so
  // leaving settings discards the scoped selection.
  return isOrganizationSettings ? (
    <SettingsEnvironmentProvider>{shell}</SettingsEnvironmentProvider>
  ) : (
    shell
  );
}
